/* ============================================================
   JO STRONG FOUNDATION — global.js
   Vanilla JS. All motion respects prefers-reduced-motion.
   ============================================================ */
(function () {
  "use strict";
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // In the Shopify theme editor, sections re-render live (no full reload). Show
  // everything immediately so nothing stays hidden while editing.
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var instant = reduced || designMode;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* ---------- Sticky header state ---------- */
  function initHeader() {
    var header = document.querySelector("[data-header]");
    if (!header) return;
    var onScroll = function () {
      if (window.scrollY > 8) header.classList.add("is-stuck");
      else header.classList.remove("is-stuck");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Mobile drawer ---------- */
  function initDrawer() {
    var burger = document.querySelector("[data-burger]");
    var drawer = document.querySelector("[data-drawer]");
    if (!burger || !drawer) return;
    var toggle = function (open) {
      var isOpen = open === undefined ? !document.body.classList.contains("menu-open") : open;
      document.body.classList.toggle("menu-open", isOpen);
      burger.setAttribute("aria-expanded", String(isOpen));
      drawer.setAttribute("aria-hidden", String(!isOpen));
    };
    burger.addEventListener("click", function () { toggle(); });
    drawer.querySelectorAll("a").forEach(function (a) {
      a.addEventListener("click", function () { toggle(false); });
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && document.body.classList.contains("menu-open")) toggle(false);
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll(".reveal, .mask-reveal");
    if (!els.length) return;
    if (instant || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
      });
    }, { threshold: 0.14, rootMargin: "0px 0px -8% 0px" });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Hero entrance + mouse parallax ---------- */
  function initHero() {
    var hero = document.querySelector("[data-hero]");
    if (!hero) return;

    if (instant) {
      hero.querySelectorAll(".hero__eyebrow, .hero__lede, .hero__cta, .hero__media").forEach(function (el) {
        el.style.opacity = 1; el.style.transform = "none";
      });
      hero.querySelectorAll(".hero__display .word").forEach(function (w) { w.style.transform = "none"; w.style.opacity = 1; });
      var m = hero.querySelector(".reveal-mask"); if (m) m.style.transform = "scaleY(0)";
      return;
    }

    var words = hero.querySelectorAll(".hero__display .word");
    var eyebrow = hero.querySelector(".hero__eyebrow");
    var lede = hero.querySelector(".hero__lede");
    var cta = hero.querySelector(".hero__cta");
    var media = hero.querySelector(".hero__media");
    var mask = hero.querySelector(".reveal-mask");

    var t = 0;
    var step = function (el, delay, fn) { setTimeout(fn, delay); };

    step(eyebrow, 120, function () { if (eyebrow) { eyebrow.style.transition = "opacity .7s var(--ease), transform .7s var(--ease)"; eyebrow.style.opacity = 1; eyebrow.style.transform = "none"; } });
    words.forEach(function (w, i) {
      setTimeout(function () {
        w.style.transition = "transform .9s cubic-bezier(0.22,1,0.36,1), opacity .7s ease";
        w.style.transform = "translateY(0)";
        w.style.opacity = 1;
      }, 320 + i * 110);
    });
    step(lede, 320 + words.length * 110 + 120, function () { if (lede) { lede.style.transition = "opacity .8s var(--ease), transform .8s var(--ease)"; lede.style.opacity = 1; lede.style.transform = "none"; } });
    step(cta, 320 + words.length * 110 + 260, function () { if (cta) { cta.style.transition = "opacity .8s var(--ease), transform .8s var(--ease)"; cta.style.opacity = 1; cta.style.transform = "none"; } });
    step(media, 260, function () {
      if (media) { media.style.transition = "opacity 1s var(--ease)"; media.style.opacity = 1; }
      if (mask) { mask.style.transition = "transform 1.1s cubic-bezier(0.22,1,0.36,1)"; mask.style.transform = "scaleY(0)"; }
    });

    /* mouse parallax on the display type + media */
    var display = hero.querySelector(".hero__display");
    var frame = hero.querySelector(".hero__media .frame");
    var raf = null, tx = 0, ty = 0, cx = 0, cy = 0;
    hero.addEventListener("mousemove", function (e) {
      var r = hero.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width - 0.5);
      ty = ((e.clientY - r.top) / r.height - 0.5);
      if (!raf) raf = requestAnimationFrame(loop);
    });
    hero.addEventListener("mouseleave", function () { tx = 0; ty = 0; if (!raf) raf = requestAnimationFrame(loop); });
    function loop() {
      cx += (tx - cx) * 0.08; cy += (ty - cy) * 0.08;
      if (display) display.style.transform = "translate(" + (cx * 14) + "px," + (cy * 10) + "px)";
      if (frame) frame.style.transform = "translate(" + (cx * -18) + "px," + (cy * -12) + "px)";
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) raf = requestAnimationFrame(loop);
      else raf = null;
    }
  }

  /* ---------- Kinetic word statement (why we exist) ---------- */
  function initStatement() {
    var stmts = document.querySelectorAll("[data-statement]");
    if (!stmts.length) return;
    stmts.forEach(function (stmt) {
      var wordsText = stmt.textContent.trim().split(/\s+/);
      var accents = (stmt.getAttribute("data-accent") || "").toLowerCase().split(",").map(function (s) { return s.trim(); });
      stmt.textContent = "";
      var spans = [];
      wordsText.forEach(function (w) {
        var s = document.createElement("span");
        s.className = "w";
        var clean = w.replace(/[.,]/g, "").toLowerCase();
        if (accents.indexOf(clean) !== -1) s.classList.add("accent");
        s.textContent = w;
        stmt.appendChild(s);
        spans.push(s);
      });
      if (instant || !("IntersectionObserver" in window)) {
        spans.forEach(function (s) { s.classList.add("on"); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            spans.forEach(function (s, i) { setTimeout(function () { s.classList.add("on"); }, i * 90); });
            io.unobserve(en.target);
          }
        });
      }, { threshold: 0.3 });
      io.observe(stmt);
    });
  }

  /* ---------- Animated counters ---------- */
  function initCounters() {
    var nums = document.querySelectorAll("[data-count]");
    if (!nums.length) return;
    var run = function (el) {
      var target = parseFloat(el.getAttribute("data-count"));
      var prefix = el.getAttribute("data-prefix") || "";
      var suffix = el.getAttribute("data-suffix") || "";
      if (instant || isNaN(target)) { el.innerHTML = prefix + format(target) + '<span class="pink">' + suffix + "</span>"; return; }
      var dur = 1600, start = null;
      function fmtStep(ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        var val = Math.floor(eased * target);
        el.innerHTML = prefix + format(val) + '<span class="pink">' + suffix + "</span>";
        if (p < 1) requestAnimationFrame(fmtStep);
        else el.innerHTML = prefix + format(target) + '<span class="pink">' + suffix + "</span>";
      }
      requestAnimationFrame(fmtStep);
    };
    function format(n) { return n.toLocaleString("en-US"); }
    if (instant || !("IntersectionObserver" in window)) { nums.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } });
    }, { threshold: 0.5 });
    nums.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Donation impact selector ---------- */
  function initDonationImpact() {
    var root = document.querySelector("[data-dimpact]");
    if (!root) return;
    var btns = root.querySelectorAll(".amount");
    var amtEl = root.querySelector("[data-dimpact-amt]");
    var descEl = root.querySelector("[data-dimpact-desc]");
    var cta = root.querySelector("[data-dimpact-cta]");
    var fallbackHref = cta ? cta.getAttribute("href") : null;
    var select = function (btn) {
      btns.forEach(function (b) { b.classList.remove("active"); b.setAttribute("aria-pressed", "false"); });
      btn.classList.add("active"); btn.setAttribute("aria-pressed", "true");
      var amt = btn.getAttribute("data-amount");
      var desc = btn.getAttribute("data-desc");
      if (amtEl) amtEl.textContent = "$" + amt;
      // Send the donor to this amount's own Square link, or the main donation link if it has none
      if (cta) cta.setAttribute("href", btn.getAttribute("data-link") || fallbackHref);
      if (descEl) {
        descEl.classList.add("out");
        setTimeout(function () { descEl.textContent = desc; descEl.classList.remove("out"); }, 320);
      }
    };
    btns.forEach(function (b) { b.addEventListener("click", function () { select(b); }); });
    if (btns.length) select(btns[Math.min(1, btns.length - 1)]);
  }

  /* ---------- Story modal ---------- */
  function initStories() {
    var modal = document.querySelector("[data-story-modal]");
    if (!modal) return;
    var cards = document.querySelectorAll("[data-story-open]");
    var mName = modal.querySelector("[data-m-name]");
    var mRole = modal.querySelector("[data-m-role]");
    var mQuote = modal.querySelector("[data-m-quote]");
    var mBody = modal.querySelector("[data-m-body]");
    var mMedia = modal.querySelector("[data-m-media]");
    var closeBtn = modal.querySelector("[data-story-close]");
    var scrim = modal.querySelector(".modal__scrim");
    var lastFocus = null;

    var open = function (card) {
      lastFocus = card;
      mName.textContent = card.getAttribute("data-name") || "";
      mRole.textContent = card.getAttribute("data-role") || "";
      mQuote.textContent = card.getAttribute("data-quote") || "";
      mBody.textContent = card.getAttribute("data-desc-full") || "";
      var img = card.getAttribute("data-img");
      if (img) mMedia.innerHTML = '<img src="' + img + '" alt="' + (card.getAttribute("data-name") || "") + '">';
      else mMedia.innerHTML = '<div class="ph">Portrait</div>';
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      closeBtn.focus();
    };
    var close = function () {
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      if (lastFocus) lastFocus.focus();
    };
    cards.forEach(function (c) {
      c.addEventListener("click", function () { open(c); });
      c.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(c); } });
    });
    if (closeBtn) closeBtn.addEventListener("click", close);
    if (scrim) scrim.addEventListener("click", close);
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && modal.classList.contains("open")) close(); });
  }

  /* ---------- Event flyer lightbox ---------- */
  // Delegated so it keeps working when the theme editor re-renders the section.
  function initFlyerLightbox() {
    var dialog = document.createElement("dialog");
    dialog.className = "flyerbox";
    dialog.setAttribute("aria-label", "Event flyer");
    dialog.innerHTML = '<button type="button" class="flyerbox__close" aria-label="Close flyer">&times;</button><img class="flyerbox__img" alt="">';
    document.body.appendChild(dialog);
    var img = dialog.querySelector(".flyerbox__img");
    if (typeof dialog.showModal !== "function") return; // very old browsers: link opens the image instead

    document.addEventListener("click", function (e) {
      var link = e.target.closest && e.target.closest("[data-flyer]");
      if (!link) return;
      e.preventDefault();
      var thumb = link.querySelector("img");
      var full = link.getAttribute("href");
      // Show the already-loaded thumbnail instantly, then swap in the full-size flyer
      img.src = (thumb && thumb.currentSrc) || full;
      img.alt = thumb ? thumb.alt : "";
      var hi = new Image();
      hi.onload = function () { if (dialog.open) img.src = full; };
      hi.src = full;
      dialog.showModal();
      document.body.style.overflow = "hidden";
    });
    dialog.addEventListener("click", function (e) {
      // Close on the close button or a click outside the flyer image
      if (e.target !== img) dialog.close();
    });
    dialog.addEventListener("close", function () {
      document.body.style.overflow = "";
      img.removeAttribute("src");
    });
  }

  /* ---------- Simple contact/newsletter feedback (front-end only demo) ---------- */
  function initForms() {
    document.querySelectorAll("[data-demo-form]").forEach(function (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var note = form.querySelector("[data-form-success]");
        if (note) { note.hidden = false; }
        form.reset();
      });
    });
  }

  // Inits tied to section content — safe to re-run when the editor re-renders a section.
  function initSectional() {
    initReveal();
    initHero();
    initStatement();
    initCounters();
    initDonationImpact();
    initStories();
  }

  ready(function () {
    initHeader();   // global listeners — bound once
    initDrawer();
    initForms();
    initFlyerLightbox();
    initSectional();
  });

  // Shopify theme editor: sections re-render live without a full page reload.
  // Re-run sectional inits so freshly rendered content is revealed immediately.
  document.addEventListener("shopify:section:load", initSectional);
  document.addEventListener("shopify:section:select", initSectional);
  document.addEventListener("shopify:block:select", initSectional);
})();